import { clsx } from 'clsx'
import { Activity, Monitor } from 'lucide-react'
import { useState } from 'react'
import { CpuChart, DiskChart, MemoryChart, NetworkChart } from '@/components/infra/infra-charts'
import { SmtpBoxGrid } from '@/components/infra/smtp-box-grid'
import { ThresholdEditor } from '@/components/infra/threshold-editor'
import { TopProcesses } from '@/components/infra/top-processes'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import {
  useInfraHistory,
  useInfraSnapshot,
  useInfraThresholds,
  useUpdateThreshold,
} from '@/hooks/use-infra'
import type { InfraThreshold } from '@/lib/types'

const PERIODS = ['1h', '6h', '24h', '7d'] as const
const STALE_THRESHOLD_MS = 120_000

function thresholdVariant(
  value: number,
  metric: string,
  thresholds: InfraThreshold[],
): 'default' | 'warning' | 'danger' {
  const t = thresholds.find((th) => th.metric === metric && th.enabled)
  if (!t) return 'default'
  if (value >= t.critical) return 'danger'
  if (value >= t.warning) return 'warning'
  return 'default'
}

export function InfrastructurePage(): React.JSX.Element {
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>('1h')
  const [selectedHost, setSelectedHost] = useState('')

  const snapshotQuery = useInfraSnapshot()
  const thresholdsQuery = useInfraThresholds()
  const updateThreshold = useUpdateThreshold()

  const snapshot = snapshotQuery.data
  const thresholds = thresholdsQuery.data?.thresholds ?? []

  // Pick the control plane host, or fall back to first host
  const controlPlane = snapshot?.hosts.find((h) => h.host_type === 'control_plane')
  const activeHost = selectedHost || controlPlane?.hostname || snapshot?.hosts[0]?.hostname || ''
  const historyQuery = useInfraHistory(activeHost, period)

  const smtpBoxes = snapshot?.hosts.filter((h) => h.host_type !== 'control_plane') ?? []

  // Staleness check
  const collectedAt = snapshot?.collected_at ? new Date(snapshot.collected_at) : null
  const isStale = collectedAt ? Date.now() - collectedAt.getTime() > STALE_THRESHOLD_MS : false

  if (snapshotQuery.isLoading && !snapshot) {
    return <LoadingPage message="Loading infrastructure metrics..." />
  }

  if (!snapshot || snapshot.hosts.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Monitor className="h-6 w-6 text-admin-500" />
          <h1 className="text-2xl font-semibold text-text-primary">Infrastructure</h1>
        </div>
        <div className="rounded-xl border border-dashed border-surface-3 bg-surface-0 p-12 text-center">
          <p className="text-sm text-text-tertiary">
            No metrics collected yet. The system metrics cron job runs every 60 seconds.
          </p>
        </div>
      </div>
    )
  }

  // We already returned early if hosts is empty, so hosts[0] exists
  const firstHost = snapshot.hosts[0]
  if (!firstHost) return <LoadingPage message="Loading infrastructure metrics..." />
  const cp = controlPlane ?? firstHost

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Monitor className="h-6 w-6 text-admin-500" />
          <h1 className="text-2xl font-semibold text-text-primary">Infrastructure</h1>
          {/* Live / Stale indicator */}
          {isStale ? (
            <span className="rounded bg-warning/20 px-2 py-0.5 text-xs font-medium text-warning">
              Stale
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
              <span className="text-xs font-medium text-green-600">Live</span>
            </span>
          )}
        </div>
        {/* Period selector */}
        <div className="flex gap-1 rounded-lg border border-surface-3 bg-surface-0 p-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={clsx(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                period === p ? 'bg-admin-500 text-white' : 'text-text-secondary hover:bg-surface-1',
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Control Plane Metrics */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-text-primary">
          Control Plane — {cp.hostname}
        </h2>
        <div className="grid gap-4 md:grid-cols-4">
          <MetricCard
            label="CPU"
            value={`${cp.cpu_percent.toFixed(1)}%`}
            variant={thresholdVariant(cp.cpu_percent, 'cpu', thresholds)}
          />
          <MetricCard
            label="Memory"
            value={`${cp.memory_percent.toFixed(1)}%`}
            sub={`${cp.memory_used_mb.toLocaleString()} / ${cp.memory_total_mb.toLocaleString()} MB`}
            variant={thresholdVariant(cp.memory_percent, 'memory', thresholds)}
          />
          <MetricCard
            label="Disk"
            value={`${cp.disk_percent.toFixed(1)}%`}
            sub={`${cp.disk_used_gb.toFixed(1)} / ${cp.disk_total_gb.toFixed(1)} GB`}
            variant={thresholdVariant(cp.disk_percent, 'disk', thresholds)}
          />
          <MetricCard
            label="Load Avg"
            value={cp.load_1.toFixed(2)}
            sub={`5m: ${cp.load_5.toFixed(2)} | 15m: ${cp.load_15.toFixed(2)}`}
          />
        </div>
      </section>

      {/* Charts */}
      <section className="grid gap-6 lg:grid-cols-2">
        <CpuChart data={historyQuery.data?.points ?? []} />
        <MemoryChart data={historyQuery.data?.points ?? []} />
        <DiskChart data={historyQuery.data?.points ?? []} />
        <NetworkChart data={historyQuery.data?.points ?? []} />
      </section>

      {/* SMTP Boxes */}
      {smtpBoxes.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-semibold text-text-primary">SMTP Boxes</h2>
          <SmtpBoxGrid
            hosts={smtpBoxes}
            thresholds={thresholds}
            onSelect={(hostname) => setSelectedHost(hostname)}
          />
        </section>
      )}

      {/* Top Processes */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-text-primary">
          Top Processes — {activeHost}
        </h2>
        <TopProcesses
          processes={snapshot.hosts.find((h) => h.hostname === activeHost)?.top_processes ?? []}
        />
      </section>

      {/* Threshold Config */}
      {thresholds.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-semibold text-text-primary">Alert Thresholds</h2>
          <ThresholdEditor
            thresholds={thresholds}
            onSave={(t) => updateThreshold.mutate(t)}
            isSaving={updateThreshold.isPending}
          />
        </section>
      )}

      {/* Live polling indicator */}
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-green-500" />
        <span className="text-xs text-text-tertiary">
          Snapshot polling every 15s | History polling every 60s
        </span>
      </div>
    </div>
  )
}
